import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, Loader2 } from 'lucide-react';

interface FloatingAudioPlayerProps {
    audioUrl: string | null;
}

const FloatingAudioPlayer: React.FC<FloatingAudioPlayerProps> = ({ audioUrl }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const audioRef = useRef<HTMLAudioElement | null>(null);

    useEffect(() => {
        if (audioUrl) {
            audioRef.current = new Audio(audioUrl);
            audioRef.current.onended = () => setIsPlaying(false);
            audioRef.current.oncanplaythrough = () => setIsLoading(false);
            audioRef.current.onloadstart = () => setIsLoading(true);
            audioRef.current.onerror = () => {
                setIsLoading(false);
                setIsPlaying(false);
                console.error("Error loading audio");
            };
        } else {
            audioRef.current = null;
        }

        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
        };
    }, [audioUrl]);

    const togglePlay = () => {
        if (!audioRef.current) return;

        if (isPlaying) {
            console.log(`⏹️ Full conversation audio stopped`);
            audioRef.current.pause();
        } else {
            // Stop any other playing audio (if we had a global audio context, we'd use that)
            // For now, we just play this one.
            // Note: The requirement says "Stop other audio when a new one starts". 
            // This might require a global state or event bus if we want to coordinate with message players.
            // For now, let's just handle this player's state.
            console.log(`▶️ Playing full conversation audio: url=${audioUrl}`);
            audioRef.current.play().catch(e => console.error("Play error:", e));
        }
        setIsPlaying(!isPlaying);
    };

    if (!audioUrl) return null;

    return (
        <div className="absolute left-1/2 transform -translate-x-1/2 -top-16 z-10">
            <button
                onClick={togglePlay}
                className="flex items-center gap-2 px-4 py-2 bg-violet-100/20 dark:bg-violet-400/20 backdrop-blur-sm rounded-full shadow-lg border border-violet-200/30 dark:border-violet-700/30 hover:bg-violet-100/20 dark:hover:bg-violet-400/20 transition-all duration-300 ease-in-out group"
            >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isPlaying ? 'bg-red-100 text-red-600' : 'bg-violet-200/50 text-violet-600 dark:text-violet-300'}`}>
                    {isLoading ? (
                        <Loader2 size={16} className="animate-spin" />
                    ) : isPlaying ? (
                        <Pause size={16} fill="currentColor" />
                    ) : (
                        <Play size={16} fill="currentColor" className="ml-0.5" />
                    )}
                </div>
                <div className="flex flex-col items-start mr-1">
                    <span className="text-xs font-semibold text-violet-900 dark:text-violet-600">Full Conversation</span>
                    <span className="text-[10px] text-violet-700/80 dark:text-violet-300/80 font-medium">
                        {isPlaying ? 'Playing...' : 'Click to play'}
                    </span>
                </div>
            </button>
        </div>
    );
};

export default FloatingAudioPlayer;
